import React, { forwardRef, useContext } from 'react';
import { useMergeRefs, FloatingPortal } from '@floating-ui/react';

import { AppContainerContext, Comp, getContainerStylingTransientProps, useTheme } from '@redsift/design-system';
import { TooltipContentProps } from './types';
import { TooltipPlacement, useTooltipContext } from '../tooltip';
import { StyledTooltipContent } from './styles';
import classNames from 'classnames';

const COMPONENT_NAME = 'TooltipContent';
const CLASSNAME = 'redsift-tooltip-content';

/**
 * The TooltipContent component.
 */
export const TooltipContent: Comp<TooltipContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { transientProps, otherProps } = getContainerStylingTransientProps(props);

  const { children, className, style, theme: propsTheme } = otherProps;
  const appContainerState = useContext(AppContainerContext);
  const {
    getFloatingProps,
    isOpen,
    placement,
    refs,
    strategy,
    x,
    y,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useTooltipContext();
  const theme = useTheme ? useTheme(propsTheme) : undefined;
  const popoverRef = useMergeRefs([refs.setFloating, ref]);

  const { arrowRef } = useTooltipContext();

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isOpen && (
        <StyledTooltipContent
          className={classNames(TooltipContent.className, className)}
          ref={popoverRef}
          $theme={theme!}
          {...getFloatingProps(otherProps)}
          {...transientProps}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            visibility: x == null ? 'hidden' : 'visible',
            ...style,
          }}
          $placement={placement as TooltipPlacement}
        >
          <div
            ref={arrowRef}
            className={`${TooltipContent.className}__arrow`}
            style={{
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              [staticSide!]: '-6px',
            }}
          />
          <div className={`${TooltipContent.className}__inner`}>{children}</div>
        </StyledTooltipContent>
      )}
    </FloatingPortal>
  );
});
TooltipContent.className = CLASSNAME;
TooltipContent.displayName = COMPONENT_NAME;
